import { LitElement, PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'

type Constructor<T = object> = new (...args: any[]) => T

export interface HiddenFieldsOptions<F extends string> {
  hiddenByDefault?: readonly F[]
}

export declare class HiddenFieldsMixinInterface<F extends string> {
  hiddenFields: Set<F>
  protected isHidden(field: F): boolean
  protected setLegacyVisibility(attribute: string, field: F, visible: boolean): void
}

const parseHiddenFields = (value: string | null): Set<string> =>
  new Set((value ?? '').split(',').map((entry) => entry.trim()).filter(Boolean))

export const HiddenFieldsMixin = <F extends string, T extends Constructor<LitElement>>(
  superClass: T,
  fields: readonly F[],
  options: HiddenFieldsOptions<F> = {},
) => {
  const isField = (entry: string): entry is F => (fields as readonly string[]).includes(entry)
  const hiddenByDefault: readonly F[] = options.hiddenByDefault ?? []

  class HiddenFieldsMixinClass extends superClass {
    private hiddenFieldsValue: Set<F> = new Set()

    private hiddenFieldsSet = false

    private legacyVisibility = new Map<string, { field: F, visible: boolean }>()

    @property({ attribute: 'hidden-fields', converter: { fromAttribute: parseHiddenFields } })
    set hiddenFields(value: Set<F>) {
      const oldValue = this.hiddenFieldsValue
      this.hiddenFieldsValue = value
      this.hiddenFieldsSet = true
      this.requestUpdate('hiddenFields', oldValue)
    }

    get hiddenFields(): Set<F> {
      return this.hiddenFieldsValue
    }

    protected isHidden(field: F): boolean {
      const legacy = [...this.legacyVisibility.values()].filter((entry) => entry.field === field)
      if (legacy.some((entry) => !entry.visible)) return true
      if (this.hiddenFieldsValue.has(field)) return true
      if (this.hiddenFieldsSet || legacy.some((entry) => entry.visible)) return false
      return hiddenByDefault.includes(field)
    }

    protected setLegacyVisibility(attribute: string, field: F, visible: boolean): void {
      console.warn(`<${this.localName}> "${attribute}" is deprecated, use hidden-fields="${field}" to hide it instead.`)
      this.legacyVisibility.set(attribute, { field, visible })
      this.requestUpdate()
    }

    protected override willUpdate(changedProperties: PropertyValues): void {
      super.willUpdate(changedProperties)
      if (!this.hasUpdated && !this.hiddenFieldsSet && hiddenByDefault.length > 0) {
        console.warn(
          `<${this.localName}> hidden-fields is not set, so ${hiddenByDefault.join(', ')} stay hidden as before. Set hidden-fields to choose which parts to hide; in a later release every part is shown by default.`
        )
      }
      if (!changedProperties.has('hiddenFields')) return

      const entries = [...this.hiddenFieldsValue] as string[]
      const unknownFields = entries.filter((entry) => !isField(entry))
      if (unknownFields.length === 0) return

      console.warn(
        `<${this.localName}> hidden-fields contains unknown value(s): ${unknownFields.join(', ')}. Valid values are: ${fields.join(', ')}.`
      )
      this.hiddenFields = new Set(entries.filter(isField))
    }
  }

  return HiddenFieldsMixinClass as unknown as Constructor<HiddenFieldsMixinInterface<F>> & T
}
